// NutritionDaysView.tsx
import * as React from "react";
import {
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NoImageView from "../no-image-view";
import NutritionTrendsWeb from "./line-charts";
import MacroRingsWeb from "../../../home/food-ring";
import { Meal, User } from "../../../../models/types";
import GlassContainer from "../../components/liquid-glass";

/* -------------------- Helpers -------------------- */
type Totals = {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const ymd = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
// Parse "YYYY-MM-DD" as LOCAL midnight to avoid UTC drift
const parseYMDLocal = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const nz = (v?: number) => (typeof v === "number" && !isNaN(v) ? v : 0);

const mealTotals = (meals: Meal[] | Meal): Totals => {
  const arr = Array.isArray(meals) ? meals : [meals];
  return arr.reduce(
    (acc, m) => {
      const s =
        typeof m.servings === "number" && !isNaN(m.servings) ? m.servings : 1;
      acc.calories += Math.round(nz(m.calories) * s);
      acc.protein += nz(m.protein) * s;
      acc.carbs += nz(m.carbs) * s;
      acc.fats += nz(m.fats) * s;
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fats: 0 } as Totals
  );
};

const daysBetweenInclusive = (start: Date, end: Date) => {
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  const MS_PER_DAY = 86_400_000;
  return Math.max(1, Math.floor((e - s) / MS_PER_DAY) + 1);
};

const formatDayTitle = (d: Date) =>
  d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

/* -------------------- Presets (for optional local controls) -------------------- */
type Preset = "TODAY" | "P7" | "PMONTH" | "PYEAR" | "CUSTOM";
const getRangeForPreset = (p: Preset): { start: Date; end: Date } => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  switch (p) {
    case "TODAY":
      return { start: todayStart, end: todayEnd };
    case "P7":
      return { start: startOfDay(addDays(now, -6)), end: todayEnd };
    case "PMONTH":
      return { start: startOfDay(addDays(now, -29)), end: todayEnd };
    case "PYEAR":
      return { start: startOfDay(addDays(now, -364)), end: todayEnd };
    case "CUSTOM":
    default:
      return { start: todayStart, end: todayEnd };
  }
};

/* -------------------- Chip styles -------------------- */
const chipStyles = {
  calories: { bgcolor: "#1b5e20", color: "#ffffff" },
  protein: { bgcolor: "#d32f2f", color: "#ffffff" },
  fats: { bgcolor: "#1976d2", color: "#ffffff" },
  carbs: { bgcolor: "#fbc02d", color: "#000000" },
};

/* -------------------- Glassy dialog paper -------------------- */
const glassPaperSx = (mode: "light" | "dark") => ({
  borderRadius: 4,
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.18)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  boxShadow: "0 24px 48px rgba(0,0,0,0.25)",
  background:
    mode === "dark"
      ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.15) 100%)",
});

/* -------------------- Component -------------------- */
export type DateRange = { start: Date; end: Date };

interface NutritionDaysViewProps {
  meals: Meal[];
  user?: User; // optional (goals for chart goal-lines)
  title?: string;
  /** If provided, component becomes controlled and uses this unified range */
  dateRange?: DateRange;
  /** If true, show local preset chips/dialog; default false (use unified header) */
  showControls?: boolean;
}

export default function NutritionDaysView({
  meals,
  user,
  title = "Nutrition",
  dateRange,
  showControls = false,
}: NutritionDaysViewProps) {
  const theme = useTheme();

  // Local controls (only used if showControls === true AND no external dateRange)
  const [preset, setPreset] = React.useState<Preset>("TODAY");
  const [customOpen, setCustomOpen] = React.useState(false);
  const [customStart, setCustomStart] = React.useState<string>(ymd(new Date()));
  const [customEnd, setCustomEnd] = React.useState<string>(ymd(new Date()));

  // Track which meal is "hovered" per day to drive ring reanimation
  const [hoveredByDay, setHoveredByDay] = React.useState<
    Record<string, { totals: Totals; id: string } | null>
  >({});

  // Meal detail dialog
  const [selectedMeal, setSelectedMeal] = React.useState<Meal | null>(null);

  // Effective range:
  // - If parent passes dateRange, it wins (unified header controls)
  // - Else, derive from local preset/custom (when showControls is enabled)
  const effectiveRange = React.useMemo(() => {
    if (dateRange) return dateRange;

    // fallback to local (old behavior)
    if (preset !== "CUSTOM") return getRangeForPreset(preset);
    // parse YYYY-MM-DD safely as local
    const sParts = customStart.split("-").map(Number);
    const eParts = customEnd.split("-").map(Number);
    const s = startOfDay(new Date(sParts[0], sParts[1] - 1, sParts[2]));
    const e = endOfDay(new Date(eParts[0], eParts[1] - 1, eParts[2]));
    return { start: s, end: e };
  }, [dateRange, preset, customStart, customEnd]);

  const { dayKeys, byDay } = React.useMemo(() => {
    const filtered = (meals ?? []).filter((m) => {
      const d = new Date(m.dateTime); // ISO -> local
      return d >= effectiveRange.start && d <= effectiveRange.end;
    });

    // group by local day
    const byDay: Record<string, Meal[]> = {};
    for (const meal of filtered) {
      const key = ymd(new Date(meal.dateTime)); // y-m-d in local
      if (!byDay[key]) byDay[key] = [];
      byDay[key].push(meal);
    }

    // sort keys desc using local parsing to avoid UTC drift
    const dayKeys = Object.keys(byDay).sort(
      (a, b) => parseYMDLocal(b).getTime() - parseYMDLocal(a).getTime()
    );

    return { dayKeys, byDay };
  }, [meals, effectiveRange]);

  const handlePreset = (p: Preset) => {
    if (!showControls || dateRange) return; // ignore clicks when controlled
    if (p === "CUSTOM") setCustomOpen(true);
    else setPreset(p);
  };

  const applyCustom = () => {
    if (!showControls || dateRange) return;
    setPreset("CUSTOM");
    setCustomOpen(false);
  };

  const daysInRange = React.useMemo(
    () => daysBetweenInclusive(effectiveRange.start, effectiveRange.end),
    [effectiveRange]
  );

  const shouldShowGraphs = daysInRange > 2;

  // When no user is provided, fabricate a minimal shape so the charts still render
  const chartsUser: User =
    user ??
    ({
      userId: "anon",
      name: "",
      email: "",
      templates: [],
      workouts: [],
      isProUser: false,
      meals: meals ?? [],
      waterEntries: [],
      goals: {
        caloriesGoal: 2500,
        proteinGoal: 160,
        carbsGoal: 300,
        fatsGoal: 85,
        trainingGoal: "maintain",
      },
      height: 0,
      weight: 0,
      gender: "male",
      birthday: new Date("2000-01-01"),
      activityLevel: "moderately active",
      isDietMode: false,
      isMetricUnits: true,
      isReportsOn: false,
      partnerClients: [],
    } as unknown as User);

  const goals = (user?.goals ?? chartsUser.goals) || {
    caloriesGoal: 0,
    proteinGoal: 0,
    carbsGoal: 0,
    fatsGoal: 0,
  };

  const onMealRowClick = (meal: Meal) => setSelectedMeal(meal);

  return (
    <Box sx={{ mb: 20 }}>
      {/* Charts + Rings (only when the range spans >2 days) */}
      {shouldShowGraphs && (
        <NutritionTrendsWeb
          user={chartsUser}
          rangeStart={effectiveRange.start}
          rangeEnd={effectiveRange.end}
          showGoals={!!user}
        />
      )}

      {/* Days as glass-wrapped accordions */}
      {dayKeys.length === 0 ? (
        <Typography sx={{ mt: 2 }}>No meals in this range.</Typography>
      ) : (
        dayKeys.map((dk) => {
          const mealsForDay = byDay[dk];
          const dateObj = parseYMDLocal(dk); // local-safe
          const weekday = dateObj.toLocaleDateString(undefined, {
            weekday: "long",
          });
          const monthDay = dateObj.toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
          });
          const dayTotals = mealTotals(mealsForDay);
          const hovered = hoveredByDay[dk];
          const ringTotals = hovered?.totals ?? dayTotals;
          const ringKey = hovered ? `meal-${hovered.id}` : `day-${dk}`;

          return (
            <GlassContainer
              key={dk}
              blur={20}
              radius={18}
              sx={{
                mt: 2,
                flexDirection: "column",
              }}
            >
              <Accordion
                disableGutters
                sx={{
                  width: "100%",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 1.5,
                    "& .MuiAccordionSummary-content": { m: 0, width: "100%" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    {/* Left: Date + count */}
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        {weekday}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {monthDay} • {mealsForDay.length} meal
                        {mealsForDay.length > 1 ? "s" : ""}
                      </Typography>
                    </Box>

                    {/* Right: Totals (calories + macros) */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 280,
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${dayTotals.calories} kcal`}
                        sx={chipStyles.calories}
                      />
                      <Chip
                        size="small"
                        label={`${Math.round(dayTotals.protein)}g P`}
                        sx={chipStyles.protein}
                      />
                      <Chip
                        size="small"
                        label={`${Math.round(dayTotals.carbs)}g C`}
                        sx={chipStyles.carbs}
                      />
                      <Chip
                        size="small"
                        label={`${Math.round(dayTotals.fats)}g F`}
                        sx={chipStyles.fats}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      px: 2,
                      pb: 2,
                      flexWrap: { xs: "wrap", md: "nowrap" },
                    }}
                  >
                    {/* Left column: Meal rows */}
                    <Box sx={{ flex: 2, minWidth: 320 }}>
                      {mealsForDay.map((meal, idx) => {
                        const s =
                          typeof meal.servings === "number" &&
                            !isNaN(meal.servings)
                            ? meal.servings
                            : 1;
                        const cals = Math.round(nz(meal.calories) * s);
                        const prot = Math.round(nz(meal.protein) * s);
                        const carbs = Math.round(nz(meal.carbs) * s);
                        const fats = Math.round(nz(meal.fats) * s);

                        const thisMealTotals: Totals = {
                          calories: cals,
                          protein: prot,
                          carbs,
                          fats,
                        };
                        const thumb = meal.id?.toString?.() ?? "";
                        const isUsda = thumb.includes("/usda-meal");

                        return (
                          <Box key={meal.id}>
                            <Box
                              onClick={() => onMealRowClick(meal)}
                              onMouseEnter={() =>
                                setHoveredByDay((prev) => ({
                                  ...prev,
                                  [dk]: { totals: thisMealTotals, id: meal.id },
                                }))
                              }
                              onMouseLeave={() =>
                                setHoveredByDay((prev) => ({
                                  ...prev,
                                  [dk]: null,
                                }))
                              }
                              sx={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                px: 1,
                                py: 1.15,
                                borderRadius: 2,
                                transition:
                                  "background-color 0.2s ease, transform 0.08s ease",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "light"
                                      ? "rgba(0,0,0,0.04)"
                                      : "rgba(255,255,255,0.06)",
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                              {/* Thumbnail */}
                              {isUsda ? (
                                <Box
                                  sx={{
                                    width: 84,
                                    height: 84,
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    flexShrink: 0,
                                  }}
                                >
                                  <NoImageView
                                    text={thumb}
                                    radius={12}
                                    height={84}
                                    width={84}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  component="img"
                                  src={thumb}
                                  alt={meal.name}
                                  sx={{
                                    width: 84,
                                    height: 84,
                                    objectFit: "cover",
                                    borderRadius: 2,
                                    flexShrink: 0,
                                  }}
                                />
                              )}

                              {/* Name + chips */}
                              <Box sx={{ flex: 1, minWidth: 160 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontWeight: 600,
                                  }}
                                >
                                  {meal.name}
                                </Typography>
                                {s !== 1 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {s} serving{s > 1 ? "s" : ""}
                                  </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    pr: 1,
                                    mt: 1,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={`${cals} kcal`}
                                    sx={chipStyles.calories}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${prot}g P`}
                                    sx={chipStyles.protein}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${fats}g F`}
                                    sx={chipStyles.fats}
                                  />
                                  <Chip
                                    size="small"
                                    label={`${carbs}g C`}
                                    sx={chipStyles.carbs}
                                  />
                                </Box>
                              </Box>

                              {/* Time */}
                              <Box sx={{ justifySelf: "flex-end", flex: 1 }}>
                                <Typography sx={{ textAlign: "right" }}>
                                  {new Intl.DateTimeFormat("en-US", {
                                    hour: "numeric",
                                    minute: "numeric",
                                    hour12: true,
                                  }).format(new Date(meal.dateTime))}
                                </Typography>
                              </Box>
                            </Box>
                            {idx < mealsForDay.length - 1 && <Divider />}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Right column: Macro rings (day totals by default; hover switches to meal) */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 320,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MacroRingsWeb
                        key={ringKey}
                        isLightMode={theme.palette.mode === "light"}
                        title={formatDayTitle(dateObj)}
                        caloriesToday={ringTotals.calories}
                        caloriesGoal={Math.round(nz(goals.caloriesGoal))}
                        proteinToday={Math.round(ringTotals.protein)}
                        proteinGoal={Math.round(nz(goals.proteinGoal))}
                        carbsToday={Math.round(ringTotals.carbs)}
                        carbsGoal={Math.round(nz(goals.carbsGoal))}
                        fatToday={Math.round(ringTotals.fats)}
                        fatGoal={Math.round(nz(goals.fatsGoal))}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </GlassContainer>
          );
        })
      )}

      {/* Custom range dialog (shown only when using local controls) */}
      {showControls && !dateRange && (
        <Dialog
          open={customOpen}
          onClose={() => setCustomOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: glassPaperSx(theme.palette.mode) }}
        >
          <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
            Select custom range
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <GlassContainer
              blur={18}
              radius={16}
              sx={{
                p: 2,
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} gap={2} mt={1}>
                <TextField
                  label="Start"
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            </GlassContainer>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyCustom}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Meal Details dialog */}
      <Dialog
        open={!!selectedMeal}
        onClose={() => setSelectedMeal(null)}
        fullWidth
        maxWidth="sm"
        sx={{ borderRadius: "24px" }}
        PaperProps={{ sx: glassPaperSx(theme.palette.mode) }}
      >
        {selectedMeal && (
          <GlassContainer
            sx={{ "&:hover": { transform: "scale(1)", borderRadius: "24px" } }}
          >
            <DialogContent sx={{ pt: 0 }}>
              <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
                {selectedMeal.name || "Meal details"}
              </DialogTitle>
              {/* Image */}
              <Box
                sx={{
                  height: 220,
                  overflow: "hidden",
                  borderRadius: "24px",
                  mb: 2,
                  background:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(0,0,0,0.04)",
                }}
              >
                {selectedMeal.id?.toString?.().includes("/usda-meal") ? (
                  <NoImageView
                    text={selectedMeal.id?.toString?.() ?? ""}
                    radius={16}
                    height={220}
                    width={"10%"}
                  />
                ) : (
                  <Box
                    component="img"
                    src={selectedMeal.id?.toString?.()}
                    alt={selectedMeal.name}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
              </Box>

              {/* Macros row */}
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Chip
                  size="small"
                  label={`${Math.round(nz(selectedMeal.calories))} kcal`}
                  sx={chipStyles.calories}
                />
                <Chip
                  size="small"
                  label={`${Math.round(nz(selectedMeal.protein))}g P`}
                  sx={chipStyles.protein}
                />
                <Chip
                  size="small"
                  label={`${Math.round(nz(selectedMeal.carbs))}g C`}
                  sx={chipStyles.carbs}
                />
                <Chip
                  size="small"
                  label={`${Math.round(nz(selectedMeal.fats))}g F`}
                  sx={chipStyles.fats}
                />
              </Stack>

              <Divider sx={{ my: 2, opacity: 0.6 }} />

              {/* Meta */}
              <Stack direction="row" gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  {new Intl.DateTimeFormat("en-US", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(selectedMeal.dateTime))}
                </Typography>
                {typeof selectedMeal.servings === "number" && (
                  <Typography variant="body2" color="text.secondary">
                    Servings: {selectedMeal.servings}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
          </GlassContainer>
        )}
      </Dialog>
    </Box>
  );
}
